/**
 * DeepBlue Arbitraj Sayfası
 * DeepBook V3 Margin + Bluefin Perps funding arbitrajı yönetimi
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Zap, Settings, TrendingUp, TrendingDown, Clock,
  CheckCircle, XCircle, AlertTriangle, Trash2, X,
  ChevronDown, ChevronRight, RefreshCw, ArrowDownToLine, ExternalLink,
} from 'lucide-react';
import { getTheme } from '../App';
import { useWS } from '../WSContext';

interface Props { dark?: boolean; }

const STATUS_COLORS: Record<string, string> = {
  opening:    'text-yellow-400',
  open:       'text-emerald-400',
  closing:    'text-blue-400',
  partial_tp: 'text-orange-400',
  cancelled:  'text-gray-500',
  closed:     'text-gray-500',
};

const STATUS_LABELS: Record<string, string> = {
  opening:    'Açılıyor',
  open:       'Açık',
  closing:    'Kapatılıyor',
  partial_tp: 'Kısmi TP',
  cancelled:  'İptal',
  closed:     'Kapandı',
};

const DIRECTION_LABEL: Record<string, string> = {
  long_db_short_bf:  'DB Long / BF Short',
  short_db_long_bf:  'DB Short / BF Long',
};

const fmtPrice = (v: number | undefined) =>
  !v ? '—' : v < 1 ? v.toFixed(5) : v.toFixed(4);

const fmtPct = (v: number | undefined) =>
  !v ? '—' : `${(v * 100).toFixed(4)}%`;

const fmtTime = (ts: number | undefined) => {
  if (!ts) return '—';
  return new Date(ts * 1000).toLocaleTimeString('tr-TR');
};

function StatusBadge({ status }: { status: string }) {
  const icon =
    status === 'open'       ? <CheckCircle size={11} /> :
    status === 'opening'    ? <Clock size={11} /> :
    status === 'cancelled'  ? <XCircle size={11} /> :
    status === 'partial_tp' ? <AlertTriangle size={11} /> :
    <RefreshCw size={11} />;
  return (
    <span className={`flex items-center gap-1 text-[10px] font-bold ${STATUS_COLORS[status] || 'text-gray-400'}`}>
      {icon} {STATUS_LABELS[status] || status}
    </span>
  );
}

function LegStatus({ filled, failed, label }: { filled: boolean; failed: boolean; label: string }) {
  return (
    <span className={`text-[10px] ${
      failed   ? 'text-gray-500 line-through' :
      filled   ? 'text-emerald-400' :
                 'text-yellow-400'
    }`}>
      {label}: {failed ? 'Başarısız' : filled ? 'Doldu' : 'Bekliyor'}
    </span>
  );
}

// ─── Deposit modal ───────────────────────────────────────────────────────────

const PAIRS = ['SUI_USDC', 'WAL_USDC', 'DEEP_USDC', 'XBTC_USDC'];

function DepositModal({ onClose, sendWs, subscribe }: {
  onClose: () => void;
  sendWs: (m: any) => void;
  subscribe: (fn: (d: any) => void) => () => void;
}) {
  const [tab,     setTab]     = useState<'deepbook' | 'bluefin'>('deepbook');
  const [poolKey, setPoolKey] = useState('SUI_USDC');
  const [amount,  setAmount]  = useState('');
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState<{ ok: boolean; msg: string } | null>(null);

  // Deposit sonucunu dinle
  useEffect(() => {
    return subscribe((data: any) => {
      if (data.type === 'deposit_result') {
        setLoading(false);
        const ok = data.payload?.success === true;
        const txDigest = data.payload?.data?.digest || data.payload?.data?.txDigest || '';
        setResult({
          ok,
          msg: ok
            ? `✓ Başarılı${txDigest ? ` — tx: ${txDigest.slice(0, 16)}…` : ''}`
            : `Hata: ${data.payload?.error || 'Bilinmeyen hata'}`,
        });
      }
    });
  }, [subscribe]);

  const submit = () => {
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) return;
    setLoading(true);
    setResult(null);
    if (tab === 'deepbook') {
      sendWs({ type: 'db_deposit', payload: { poolKey, amount: Number(amount) } });
    } else {
      sendWs({ type: 'bf_deposit', payload: { amount: Number(amount) } });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-[#061530] border border-[#0a2560] rounded-2xl w-full max-w-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <ArrowDownToLine size={14} className="text-[#4A9FE8]" /> Teminat Yatır
          </h3>
          <button onClick={onClose} className="text-blue-500 hover:text-white"><X size={16} /></button>
        </div>

        {/* Tab */}
        <div className="flex rounded-lg overflow-hidden border border-[#0a2560]">
          {(['deepbook', 'bluefin'] as const).map(t => (
            <button key={t} onClick={() => { setTab(t); setResult(null); setAmount(''); }}
              className={`flex-1 py-2 text-xs font-bold transition-colors ${
                tab === t ? 'bg-[#003087] text-white' : 'bg-[#0a2560]/40 text-blue-400 hover:text-white'
              }`}>
              {t === 'deepbook' ? 'DeepBook' : 'Bluefin'}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {tab === 'deepbook' && (
            <div>
              <label className="text-[11px] text-blue-400">Margin Manager (Havuz)</label>
              <select value={poolKey} onChange={e => setPoolKey(e.target.value)}
                className="w-full mt-1 px-3 py-1.5 rounded-lg bg-[#0a2560]/60 border border-[#003087] text-white text-xs outline-none focus:border-[#4A9FE8]">
                {PAIRS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-[11px] text-blue-400">Miktar (USDC)</label>
            <input type="number" min="1" step="1" placeholder="100"
              value={amount} onChange={e => setAmount(e.target.value)}
              className="w-full mt-1 px-3 py-1.5 rounded-lg bg-[#0a2560]/60 border border-[#003087] text-white text-xs outline-none focus:border-[#4A9FE8]"
            />
          </div>
          <p className="text-[10px] text-blue-500">
            {tab === 'deepbook'
              ? 'USDC cüzdanından seçili margin manager\'a on-chain yatırılır. Her havuz için ayrı teminat gerekir.'
              : 'USDC cüzdanından Bluefin cross-margin hesabına on-chain yatırılır. Tüm perp pozisyonları bu bakiyeyi kullanır.'}
          </p>
        </div>

        {result && (
          <div className={`text-[11px] px-3 py-2 rounded-lg font-mono ${
            result.ok
              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
              : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
          }`}>
            {result.msg}
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button onClick={onClose}
            className="flex-1 py-2 rounded-lg bg-[#0a2560] text-blue-300 text-xs hover:bg-[#0d3080]">
            İptal
          </button>
          <button onClick={submit} disabled={loading || !amount}
            className="flex-1 py-2 rounded-lg bg-[#003087] text-white text-xs hover:bg-[#0057A8] font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5">
            {loading
              ? <><RefreshCw size={11} className="animate-spin" /> İşleniyor…</>
              : <><ArrowDownToLine size={11} /> Yatır</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Config modal ────────────────────────────────────────────────────────────

function ConfigModal({ config, tickers, onSave, onClose }: {
  config: any;
  tickers: any[];
  onSave: (c: any) => void;
  onClose: () => void;
}) {
  const [local, setLocal] = useState({ ...config });
  const set = (k: string, v: any) => setLocal((p: any) => ({ ...p, [k]: v }));

  // Seçili pair'ın ticker'ı
  const activeTicker = tickers.find((t: any) => t.pool_key === local.active_pair);
  const estFunding   = activeTicker?.est_funding ?? null;
  const nextFunding  = activeTicker?.next_funding ?? null;

  // Her giriş adımının USD değeri
  const stepNotional = local.max_notional && local.entry_step_pct
    ? (local.max_notional * local.entry_step_pct / 100).toFixed(2)
    : '—';

  const inputCls = "w-full mt-1 px-3 py-1.5 rounded-lg bg-[#0a2560]/60 border border-[#003087] text-white text-xs focus:border-[#4A9FE8] outline-none";
  const labelCls = "text-[11px] text-blue-400";
  const hintCls  = "text-[10px] text-blue-600 mt-0.5";

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-[#061530] border border-[#0a2560] rounded-2xl w-full max-w-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <Settings size={14} className="text-[#4A9FE8]" /> DeepBlue Konfigürasyon
          </h3>
          <button onClick={onClose} className="text-blue-500 hover:text-white"><X size={16} /></button>
        </div>

        {/* ── Varlık seçimi ── */}
        <div>
          <label className={labelCls}>Aktif Varlık</label>
          <select
            value={local.active_pair || ''}
            onChange={e => set('active_pair', e.target.value)}
            className={`${inputCls} mt-1`}
          >
            {PAIRS.map(p => (
              <option key={p} value={p}>{p.replace('_USDC', '')} / USDC</option>
            ))}
          </select>

          {/* Funding bilgisi */}
          {activeTicker ? (
            <div className="mt-2 flex gap-3 px-3 py-2 rounded-lg bg-[#0a2560]/40 border border-[#0a2560]">
              <div className="flex-1">
                <p className="text-[9px] text-blue-500 uppercase tracking-wider">Est. Funding</p>
                <p className={`text-xs font-bold mt-0.5 ${
                  estFunding > 0 ? 'text-emerald-400' : estFunding < 0 ? 'text-rose-400' : 'text-gray-400'
                }`}>
                  {estFunding !== null ? `${(estFunding * 100).toFixed(4)}%` : '—'}
                </p>
              </div>
              <div className="flex-1">
                <p className="text-[9px] text-blue-500 uppercase tracking-wider">Sonraki Funding</p>
                <p className="text-xs font-bold text-blue-300 mt-0.5">
                  {nextFunding ? new Date(nextFunding * 1000).toLocaleTimeString('tr-TR') : '—'}
                </p>
              </div>
              <div className="flex-1">
                <p className="text-[9px] text-blue-500 uppercase tracking-wider">Oracle</p>
                <p className="text-xs font-bold text-amber-300 mt-0.5">
                  {fmtPrice(activeTicker.bf_oracle_price)}
                </p>
              </div>
              <div className="flex-1">
                <p className="text-[9px] text-blue-500 uppercase tracking-wider">Mark</p>
                <p className="text-xs font-bold text-white mt-0.5">
                  {fmtPrice(activeTicker.bf_mark_price)}
                </p>
              </div>
              <div className="flex-1">
                <p className="text-[9px] text-blue-500 uppercase tracking-wider">Market</p>
                <p className="text-xs font-bold text-sky-300 mt-0.5">
                  {fmtPrice(activeTicker.bf_market_price)}
                </p>
              </div>
              {activeTicker.bf_oracle_price > 0 && activeTicker.bf_mark_price > 0 && (() => {
                const diff = activeTicker.bf_mark_price - activeTicker.bf_oracle_price;
                const pos  = diff >= 0;
                return (
                  <div className="flex-1">
                    <p className="text-[9px] text-blue-500 uppercase tracking-wider">Mark−Oracle</p>
                    <p className={`text-xs font-bold mt-0.5 ${pos ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {pos ? '+' : ''}{diff.toFixed(5)}
                    </p>
                  </div>
                );
              })()}
            </div>
          ) : (
            <p className={`${hintCls} mt-1`}>Ticker verisi bekleniyor…</p>
          )}
        </div>

        <div className="border-t border-[#0a2560]/60 pt-1" />

        {/* ── Fiyat parametreleri ── */}
        <div className="space-y-3">
          <div>
            <label className={labelCls}>Funding Eşiği</label>
            <input type="number" step="0.00001" value={local.funding_threshold ?? 0.0001}
              onChange={e => set('funding_threshold', parseFloat(e.target.value))}
              className={inputCls}
            />
            <p className={hintCls}>
              Giriş için minimum funding: {fmtPct(local.funding_threshold)}
              {estFunding !== null && (
                <span className={`ml-2 font-bold ${
                  Math.abs(estFunding) >= (local.funding_threshold ?? 0)
                    ? 'text-emerald-400' : 'text-rose-400'
                }`}>
                  {Math.abs(estFunding) >= (local.funding_threshold ?? 0) ? '✓ eşik aşıldı' : '✗ eşik aşılmadı'}
                </span>
              )}
            </p>
          </div>

          <div>
            <label className={labelCls}>Giriş Spread'i</label>
            {/* DB = BF mark ± spread görsel */}
            <div className="mt-1 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0a2560]/30 border border-[#003087]/60 text-[11px] font-mono text-blue-300">
              <span className="text-blue-500">DB</span>
              <span className="text-blue-600">=</span>
              <span className="text-blue-500">BF mark</span>
              <span className="text-blue-600">±</span>
              <input
                type="number" step="0.0001"
                value={local.entry_spread ?? 0.0003}
                onChange={e => set('entry_spread', parseFloat(e.target.value))}
                className="w-24 bg-transparent border-b border-[#4A9FE8]/50 text-[#4A9FE8] outline-none text-center"
              />
            </div>
            {activeTicker && (
              <p className={hintCls}>
                Örnek: DB&nbsp;
                <span className="text-emerald-400 font-mono">
                  {fmtPrice((activeTicker.bf_mark_price || 0) - (local.entry_spread ?? 0.0003))}
                </span>
                &nbsp;·&nbsp;BF&nbsp;
                <span className="text-rose-400 font-mono">
                  {fmtPrice((activeTicker.bf_mark_price || 0) + (local.entry_spread ?? 0.0003))}
                </span>
              </p>
            )}
          </div>

          <div>
            <label className={labelCls}>Max Spread (her iki borsa)</label>
            <input type="number" step="0.0001" min="0"
              value={local.max_spread ?? 0.005}
              onChange={e => set('max_spread', parseFloat(e.target.value))}
              className={inputCls}
            />
            {activeTicker ? (() => {
              const dbSp = (activeTicker.db_best_ask || 0) - (activeTicker.db_best_bid || 0);
              const bfSp = (activeTicker.bf_best_ask || 0) - (activeTicker.bf_best_bid || 0);
              const maxSp = local.max_spread ?? 0.005;
              const dbOk = dbSp <= maxSp;
              const bfOk = bfSp <= maxSp;
              return (
                <p className={hintCls}>
                  DB&nbsp;<span className={`font-mono font-bold ${dbOk ? 'text-emerald-400' : 'text-rose-400'}`}>{dbSp.toFixed(5)}</span>
                  &nbsp;·&nbsp;
                  BF&nbsp;<span className={`font-mono font-bold ${bfOk ? 'text-emerald-400' : 'text-rose-400'}`}>{bfSp.toFixed(5)}</span>
                  &nbsp;
                  <span className={dbOk && bfOk ? 'text-emerald-400' : 'text-rose-400'}>
                    {dbOk && bfOk ? '✓ giriş açık' : '✗ giriş bloklu'}
                  </span>
                </p>
              );
            })() : null}
          </div>

          <div className="border-t border-[#0a2560]/60 pt-1" />

          {/* ── Pozisyon boyutu ── */}
          <div>
            <label className={labelCls}>Maksimum Pozisyon (USD)</label>
            <input type="number" step="50" min="10" value={local.max_notional ?? 500}
              onChange={e => set('max_notional', parseFloat(e.target.value))}
              className={inputCls}
            />
          </div>

          <div>
            <label className={labelCls}>Kademeli Açılış Oranı (%)</label>
            <input type="number" step="5" min="1" max="100" value={local.entry_step_pct ?? 20}
              onChange={e => set('entry_step_pct', parseFloat(e.target.value))}
              className={inputCls}
            />
            <p className={hintCls}>
              Her fırsatta açılacak tutar: <span className="text-[#4A9FE8] font-bold">${stepNotional}</span>
              {' '}({local.entry_step_pct}% × ${local.max_notional})
            </p>
          </div>

          <div className="border-t border-[#0a2560]/60 pt-1" />

          {/* ── Emir parametreleri ── */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelCls}>Leg Timeout (sn)</label>
              <input type="number" step="5" min="5" value={local.leg_timeout_s ?? 30}
                onChange={e => set('leg_timeout_s', parseInt(e.target.value))}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Kaldıraç (BF)</label>
              <input type="number" step="1" min="1" max="20" value={local.leverage ?? 3}
                onChange={e => set('leverage', parseInt(e.target.value))}
                className={inputCls}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-2 pt-1">
          <button onClick={onClose}
            className="flex-1 py-2 rounded-lg bg-[#0a2560] text-blue-300 text-xs hover:bg-[#0d3080]">
            İptal
          </button>
          <button onClick={() => { onSave(local); onClose(); }}
            className="flex-1 py-2 rounded-lg bg-[#003087] text-white text-xs hover:bg-[#0057A8] font-bold">
            Kaydet
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Arb pozisyon kartı ───────────────────────────────────────────────────────

function ArbCard({ arb, onClose, onAbort, onDelete, onPartialClose, sendWs, subscribe }: {
  arb: any;
  onClose: () => void;
  onAbort: () => void;
  onDelete: () => void;
  onPartialClose: (pct: number, spread: number) => void;
  sendWs: (m: any) => void;
  subscribe: (fn: (d: any) => void) => () => void;
}) {
  const [expanded,    setExpanded]    = useState(false);
  const [confirmDel,  setConfirmDel]  = useState(false);
  const [closePanel,  setClosePanel]  = useState(false);
  const [closePct,    setClosePct]    = useState('20');
  const [closeSpread, setCloseSpread] = useState('0.0003');
  const [closeResult, setCloseResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [closing,     setClosing]     = useState(false);

  // partial_close_result dinle
  useEffect(() => {
    return subscribe((data: any) => {
      if (data.type === 'partial_close_result') {
        setClosing(false);
        const p = data.payload || {};
        if (p.success) {
          setCloseResult({
            ok: true,
            msg: `✓ DB@${p.db_price} BF@${p.bf_price} qty=${p.qty_to_close}`,
          });
        } else {
          const errs = [p.db_error, p.bf_error, p.error].filter(Boolean).join(' | ');
          setCloseResult({ ok: false, msg: errs || 'Hata' });
        }
      }
    });
  }, [subscribe]);

  const submitPartialClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    const pct    = parseFloat(closePct);
    const spread = parseFloat(closeSpread);
    if (!pct || pct <= 0 || pct > 100) return;
    if (isNaN(spread)) return;
    setClosing(true);
    setCloseResult(null);
    onPartialClose(pct, spread);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirmDel) { setConfirmDel(true); return; }
    onDelete();
  };

  return (
    <div className="bg-[#061530]/80 border border-[#0a2560] rounded-xl overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-[#0a2560]/30"
        onClick={() => { setExpanded(!expanded); setConfirmDel(false); }}
      >
        <div className="flex items-center gap-3">
          <button onClick={e => { e.stopPropagation(); setExpanded(!expanded); }}>
            {expanded ? <ChevronDown size={14} className="text-blue-500" /> : <ChevronRight size={14} className="text-blue-500" />}
          </button>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-white">{arb.pool_key}</span>
              <StatusBadge status={arb.status} />
            </div>
            <p className="text-[11px] text-blue-500 mt-0.5">
              {DIRECTION_LABEL[arb.direction] || arb.direction}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="text-right mr-1">
            <p className="text-xs font-bold text-[#4A9FE8]">${arb.notional}</p>
            <p className="text-[10px] text-blue-500">{fmtTime(arb.created_at)}</p>
          </div>

          {/* Açık: kademeli kapat + tam kapat */}
          {arb.status === 'open' && (
            <>
              <button
                onClick={e => { e.stopPropagation(); setClosePanel(v => !v); setCloseResult(null); }}
                className={`px-2 py-1 rounded text-[10px] font-bold border transition-colors ${
                  closePanel
                    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                    : 'bg-[#0a2560] text-blue-300 border-[#003087] hover:border-emerald-500/40 hover:text-emerald-300'
                }`}
              >
                Kapat ▾
              </button>
            </>
          )}

          {/* Açılıyor / Kısmi TP: Durdur butonu */}
          {(arb.status === 'opening' || arb.status === 'partial_tp') && (
            <button
              onClick={e => { e.stopPropagation(); onAbort(); }}
              className="px-2 py-1 rounded bg-orange-500/20 text-orange-400 text-[10px] font-bold border border-orange-500/30 hover:bg-orange-500/30 flex items-center gap-1"
            >
              <XCircle size={10} /> Durdur
            </button>
          )}

          {/* Silme butonu — tüm durumlar */}
          <button
            onClick={handleDelete}
            className={`p-1 rounded transition-colors ${
              confirmDel
                ? 'bg-rose-500/20 text-rose-400 border border-rose-500/40'
                : 'text-gray-600 hover:text-rose-400'
            }`}
            title={confirmDel ? 'Emin misiniz? Tekrar tıklayın' : 'Listeden sil'}
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      {/* Kademeli kapatma paneli */}
      {closePanel && arb.status === 'open' && (
        <div
          className="px-4 py-3 border-t border-emerald-500/20 bg-emerald-500/5 space-y-2"
          onClick={e => e.stopPropagation()}
        >
          <p className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider">
            Kademeli Kapatma
          </p>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="text-[10px] text-blue-400">Kapatma Oranı (%)</label>
              <input
                type="number" min="1" max="100" step="1"
                value={closePct}
                onChange={e => setClosePct(e.target.value)}
                className="w-full mt-0.5 px-2 py-1 rounded bg-[#0a2560]/80 border border-[#003087] text-white text-xs focus:border-emerald-400 outline-none"
              />
            </div>
            <div className="flex-1">
              <label className="text-[10px] text-blue-400">Spread (DB − BF)</label>
              <input
                type="number" min="0" step="0.0001"
                value={closeSpread}
                onChange={e => setCloseSpread(e.target.value)}
                className="w-full mt-0.5 px-2 py-1 rounded bg-[#0a2560]/80 border border-[#003087] text-white text-xs focus:border-emerald-400 outline-none"
              />
            </div>
            <button
              onClick={submitPartialClose}
              disabled={closing}
              className="px-3 py-1 rounded bg-emerald-600/30 text-emerald-300 text-xs font-bold border border-emerald-500/40 hover:bg-emerald-600/50 disabled:opacity-50 flex items-center gap-1"
            >
              {closing ? <RefreshCw size={10} className="animate-spin" /> : null}
              Emir At
            </button>
          </div>

          {/* Özet satırı */}
          <p className="text-[10px] text-blue-500">
            {arb.direction === 'long_db_short_bf'
              ? `DB SELL @ ref+${closeSpread}  ·  BF BUY @ ref  ·  qty = ${arb.db_qty} × ${closePct}% = ${(Number(arb.db_qty) * Number(closePct) / 100).toFixed(6)}`
              : `DB BUY @ ref  ·  BF SELL @ ref+${closeSpread}  ·  qty = ${arb.db_qty} × ${closePct}% = ${(Number(arb.db_qty) * Number(closePct) / 100).toFixed(6)}`
            }
          </p>

          {/* Sonuç */}
          {closeResult && (
            <div className={`text-[10px] px-2 py-1.5 rounded font-mono ${
              closeResult.ok
                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
            }`}>
              {closeResult.msg}
            </div>
          )}

          {/* Tam kapat */}
          <div className="flex justify-end pt-0.5">
            <button
              onClick={e => { e.stopPropagation(); onClose(); }}
              className="text-[10px] text-blue-400 hover:text-blue-300 underline"
            >
              Tamamını kapat (mevcut entry_spread kadar uzağa)
            </button>
          </div>
        </div>
      )}

      {/* Detail */}
      {expanded && (
        <div className="px-4 pb-3 border-t border-[#0a2560]/50 pt-3 grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-wider">DeepBook</p>
            <div className="space-y-1">
              <p className="text-[11px] text-blue-300">
                {arb.db_is_bid ? 'LONG' : 'SHORT'} @ {fmtPrice(arb.db_entry_price)}
              </p>
              <p className="text-[11px] text-blue-400">Qty: {arb.db_qty}</p>
              <LegStatus filled={arb.db_filled} failed={arb.db_failed} label="DB" />
              {arb.tp_db_order_id && (
                <p className="text-[10px] text-blue-400">TP aktif</p>
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-wider">Bluefin</p>
            <div className="space-y-1">
              <p className="text-[11px] text-blue-300">
                {arb.bf_side?.toUpperCase()} @ {fmtPrice(arb.bf_entry_price)}
              </p>
              <p className="text-[11px] text-blue-400">Qty: {arb.bf_qty} · {arb.bf_symbol}</p>
              <LegStatus filled={arb.bf_filled} failed={arb.bf_failed} label="BF" />
              {arb.tp_order_hash && (
                <p className="text-[10px] text-blue-400">TP aktif</p>
              )}
            </div>
          </div>
          {arb.timeout_at && arb.status === 'opening' && (
            <div className="col-span-2">
              <p className="text-[10px] text-yellow-500">
                Timeout: {fmtTime(arb.timeout_at)}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Ana sayfa ────────────────────────────────────────────────────────────────

export default function DeepBlue({ dark = true }: Props) {
  const { sendWs, connected, subscribe, pairTrades, tickers: rawTickers, account } = useWS() as any;
  const safeTickers: any[] = Array.isArray(rawTickers) ? rawTickers.filter(Boolean) : [];
  const t = getTheme(dark);

  const [config,       setConfig]      = useState<any>(null);
  const [configOpen,   setConfigOpen]  = useState(false);
  const [depositOpen,  setDepositOpen] = useState(false);
  const [arbs,         setArbs]        = useState<any[]>([]);

  // restore_state'den gelen pairTrades'i kullan
  useEffect(() => {
    if (Array.isArray(pairTrades)) setArbs(pairTrades);
  }, [pairTrades]);

  // Bağlantıda config al
  useEffect(() => {
    if (!connected) return;
    sendWs({ type: 'get_config' });
    sendWs({ type: 'get_pair_trades' });
  }, [connected]);

  // WS mesajlarını dinle
  useEffect(() => {
    return subscribe((data: any) => {
      if (data.type === 'pair_trades')      setArbs(data.payload || []);
      if (data.type === 'deepblue_config')  setConfig(data.payload);
    });
  }, [subscribe]);

  const saveConfig = useCallback((cfg: any) => {
    sendWs({ type: 'update_config', payload: cfg });
    setConfig(cfg);
  }, [sendWs]);

  const closeArb   = (id: string) => sendWs({ type: 'close_arb',   payload: { arb_id: id } });
  const abortArb   = (id: string) => sendWs({ type: 'abort_arb',   payload: { arb_id: id } });
  const deleteArb  = (id: string) => sendWs({ type: 'delete_arb',  payload: { arb_id: id } });

  // Ticker map (funded arb check)
  const tickerMap = Object.fromEntries(
    safeTickers.map((t: any) => [t.pool_key || t.symbol, t])
  );

  const activeArbs   = arbs.filter(a => ['opening', 'open', 'closing', 'partial_tp'].includes(a.status));
  const inactiveArbs = arbs.filter(a => ['cancelled', 'closed'].includes(a.status));

  return (
    <div className={`p-5 min-h-full ${t.bg} ${t.text} space-y-5`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-black text-[#4A9FE8] flex items-center gap-2">
            <Zap size={20} /> DeepBlue Arbitraj
          </h2>
          <p className="text-xs text-blue-500 mt-0.5">
            DeepBook V3 Margin + Bluefin Perps funding arbitrajı
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setDepositOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 text-xs hover:bg-emerald-600/30 hover:border-emerald-400 transition-colors"
          >
            <ArrowDownToLine size={13} /> Teminat Yatır
          </button>
          <button
            onClick={() => setConfigOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0a2560] border border-[#003087] text-blue-300 text-xs hover:border-[#4A9FE8] hover:text-[#4A9FE8] transition-colors"
          >
            <Settings size={13} /> Konfigürasyon
          </button>
        </div>
      </div>

      {/* Hesap özeti */}
      {account && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Hesap Değeri',  value: `$${account.totalAccountValue?.toFixed(2) ?? '—'}` },
            { label: 'Kullanılabilir', value: `$${account.availableBalance?.toFixed(2) ?? '—'}` },
            { label: 'Unrealized PnL', value: `$${account.crossUnrealizedPnl?.toFixed(2) ?? '—'}`,
              color: (account.crossUnrealizedPnl ?? 0) >= 0 ? 'text-emerald-400' : 'text-rose-400' },
            { label: 'Aktif Arb',     value: String(activeArbs.length) },
          ].map(item => (
            <div key={item.label} className={`bg-[#061530]/80 border border-[#0a2560] rounded-xl p-3`}>
              <p className="text-[10px] text-blue-500 uppercase tracking-wider">{item.label}</p>
              <p className={`text-base font-bold mt-1 ${item.color || 'text-white'}`}>{item.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Ticker / Funding tablosu */}
      {safeTickers.length > 0 && (
        <div className={`bg-[#061530]/80 border border-[#0a2560] rounded-xl overflow-hidden`}>
          <div className="px-4 py-2 border-b border-[#0a2560]">
            <p className="text-[11px] font-semibold text-blue-400 uppercase tracking-wider">Canlı Piyasa</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#0a2560]">
                  {['Çift', 'DB Bid', 'DB Ask', 'BF Bid', 'BF Ask', 'Oracle', 'Mark', 'Market', 'Mark−Oracle', 'Son Funding', 'Est. Funding', 'Sonraki Funding'].map(h => (
                    <th key={h} className="text-left px-4 py-2 text-[10px] text-blue-500 font-semibold uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {safeTickers.map((tk: any) => {
                  const fundingPct = (tk.est_funding || 0) * 100;
                  const fundingPos = fundingPct > 0;
                  return (
                    <tr key={tk.pool_key || tk.symbol} className="border-b border-[#0a2560]/40 hover:bg-[#0a2560]/20">
                      <td className="px-4 py-2 font-bold text-white">{tk.pool_key || tk.symbol}</td>
                      <td className="px-4 py-2 text-emerald-300 font-mono text-[11px]">{fmtPrice(tk.db_best_bid)}</td>
                      <td className="px-4 py-2 text-rose-300 font-mono text-[11px]">{fmtPrice(tk.db_best_ask)}</td>
                      <td className="px-4 py-2 text-emerald-400 font-mono text-[11px]">{fmtPrice(tk.bf_best_bid)}</td>
                      <td className="px-4 py-2 text-rose-400 font-mono text-[11px]">{fmtPrice(tk.bf_best_ask)}</td>
                      {/* Oracle (Pyth dış fiyat) */}
                      <td className="px-4 py-2 text-amber-300 font-mono text-[11px]">{fmtPrice(tk.bf_oracle_price)}</td>
                      {/* Mark (Bluefin TWAP) */}
                      <td className="px-4 py-2 text-white font-mono text-[11px]">{fmtPrice(tk.bf_mark_price)}</td>
                      {/* Market (emir kitabı mid'i) */}
                      <td className="px-4 py-2 text-sky-300 font-mono text-[11px]">{fmtPrice(tk.bf_market_price)}</td>
                      {/* Mark − Oracle */}
                      <td className={`px-4 py-2 font-mono text-[11px] ${
                        tk.bf_oracle_price && tk.bf_mark_price
                          ? (tk.bf_mark_price - tk.bf_oracle_price) >= 0 ? 'text-emerald-400' : 'text-rose-400'
                          : 'text-gray-600'
                      }`}>
                        {tk.bf_oracle_price && tk.bf_mark_price
                          ? `${(tk.bf_mark_price - tk.bf_oracle_price) >= 0 ? '+' : ''}${(tk.bf_mark_price - tk.bf_oracle_price).toFixed(5)}`
                          : '—'}
                      </td>
                      {/* Son Funding */}
                      <td className={`px-4 py-2 font-mono text-[11px] ${
                        (tk.last_funding || 0) >= 0 ? 'text-emerald-300' : 'text-rose-300'
                      }`}>
                        {tk.last_funding != null ? `${((tk.last_funding || 0) * 100).toFixed(4)}%` : '—'}
                      </td>
                      {/* Est. Funding */}
                      <td className={`px-4 py-2 font-bold flex items-center gap-1 ${fundingPos ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {fundingPos ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                        {fundingPct.toFixed(4)}%
                      </td>
                      <td className="px-4 py-2 text-blue-400 text-[10px]">
                        {tk.next_funding ? new Date(tk.next_funding * 1000).toLocaleTimeString('tr-TR') : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Aktif arbitraj pozisyonları */}
      <div>
        <p className="text-[11px] font-semibold text-blue-400 uppercase tracking-wider mb-3">
          Aktif Pozisyonlar ({activeArbs.length})
        </p>
        {activeArbs.length === 0 ? (
          <div className="bg-[#061530]/60 border border-[#0a2560] rounded-xl px-4 py-8 text-center">
            <p className="text-xs text-blue-600">Aktif arbitraj pozisyonu yok</p>
            <p className="text-[10px] text-blue-700 mt-1">
              Funding threshold aşıldığında bot otomatik giriş yapar
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {activeArbs.map(arb => (
              <ArbCard
                key={arb.id}
                arb={arb}
                onClose={() => closeArb(arb.id)}
                onAbort={() => abortArb(arb.id)}
                onDelete={() => deleteArb(arb.id)}
                onPartialClose={(pct, spread) =>
                  sendWs({ type: 'partial_close_arb', payload: { arb_id: arb.id, close_pct: pct, close_spread: spread } })
                }
                sendWs={sendWs}
                subscribe={subscribe}
              />
            ))}
          </div>
        )}
      </div>

      {/* Geçmiş pozisyonlar */}
      {inactiveArbs.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-blue-400 uppercase tracking-wider mb-3">
            Geçmiş ({inactiveArbs.length})
          </p>
          <div className="space-y-2">
            {inactiveArbs.slice(-10).reverse().map(arb => (
              <ArbCard
                key={arb.id}
                arb={arb}
                onClose={() => {}}
                onAbort={() => {}}
                onDelete={() => deleteArb(arb.id)}
                onPartialClose={() => {}}
                sendWs={sendWs}
                subscribe={subscribe}
              />
            ))}
          </div>
        </div>
      )}

      {/* Deposit modal */}
      {depositOpen && (
        <DepositModal sendWs={sendWs} subscribe={subscribe} onClose={() => setDepositOpen(false)} />
      )}

      {/* Config modal */}
      {configOpen && config && (
        <ConfigModal
          config={config}
          tickers={safeTickers}
          onSave={saveConfig}
          onClose={() => setConfigOpen(false)}
        />
      )}
    </div>
  );
}
